// src/utils/dateUtils.ts
import { Timestamp } from 'firebase/firestore';

/**
 * Formats a date into a readable string
 * @param date - Date object, string date, or Firestore Timestamp
 * @param options - Optional Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date | string | Timestamp | null | undefined,
  options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  }
): string => {
  if (!date) return '';
  
  let dateObj: Date;
  
  if (date instanceof Timestamp) {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    return '';
  }
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleDateString(undefined, options);
};

/**
 * Format a date range into a readable string
 * @param startDate - Start date (Date, string, or Timestamp)
 * @param endDate - End date (Date, string, or Timestamp)
 * @param options - Optional Intl.DateTimeFormatOptions
 * @returns Formatted date range string
 */
export const formatDateRange = (
  startDate: Date | string | Timestamp | null | undefined,
  endDate: Date | string | Timestamp | null | undefined,
  options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  }
): string => {
  const formattedStart = formatDate(startDate, options);
  const formattedEnd = formatDate(endDate, options);
  
  if (formattedStart && formattedEnd) {
    return `${formattedStart} - ${formattedEnd}`;
  } else if (formattedStart) {
    return formattedStart;
  } else if (formattedEnd) {
    return formattedEnd;
  }
  
  return '';
};